import { setClassName } from '@change-corgi/core/error';
import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import type {
	PetitionReadyToSharePostsV2Query,
	PetitionReadyToSharePostsV2QueryVariables,
} from './getPetitionReadyToSharePostsV2.graphql';
import { PetitionReadyToSharePostsV2 } from './getPetitionReadyToSharePostsV2.graphql';

export * from './getPetitionReadyToSharePostsV2.graphql';

type V2Result = NonNullable<PetitionReadyToSharePostsV2Query['petitionBySlugOrId']>['readyToSharePostsV2'];

type ReadyToSharePostsV2Success = Extract<V2Result, { __typename: 'ReadyToSharePostsSuccessV2' }>;
export type ReadyToSharePlatformPostsV2 = ReadyToSharePostsV2Success['posts'];

export class UnrecoverableError extends Error {}
setClassName(UnrecoverableError, 'UnrecoverableError');

type ReadyToSharePlatformPostV2 = ReadyToSharePlatformPostsV2[number];

type PostTypeMetadata = {
	platform: 'facebook' | 'instagram' | 'reddit';
	postTypeName: 'post' | 'story' | 'group';
};

const POST_TYPE_METADATA = {
	ReadyToShareFacebookPost: { platform: 'facebook', postTypeName: 'post' },
	ReadyToShareFacebookStory: { platform: 'facebook', postTypeName: 'story' },
	ReadyToShareFacebookGroupsPost: { platform: 'facebook', postTypeName: 'group' },
	ReadyToShareInstagramPost: { platform: 'instagram', postTypeName: 'post' },
	ReadyToShareInstagramStory: { platform: 'instagram', postTypeName: 'story' },
	ReadyToShareRedditGroupsPost: { platform: 'reddit', postTypeName: 'group' },
} as const satisfies Readonly<Record<ReadyToSharePlatformPostV2['__typename'], PostTypeMetadata>>;

export type ReadyToShareProcessedPostImage = {
	readonly url: string;
	readonly variationName: string | null;
	readonly processing: false;
};

// Extends posts with metadata; includes image metadata only when the original post has images
export type EnrichedReadyToSharePlatformPostV2<T extends ReadyToSharePlatformPostV2 = ReadyToSharePlatformPostV2> = T &
	(typeof POST_TYPE_METADATA)[T['__typename']] &
	(T extends { images: readonly unknown[] }
		? { hasImage: boolean; images: readonly ReadyToShareProcessedPostImage[] }
		: unknown);

// Results in a union of homogeneous arrays, one for each post type, rather than
// a single array of all post types.  E.g. A[] | B[] rather than (A | B)[]
export type EnrichedReadyToSharePlatformPostsV2 = {
	[K in ReadyToSharePlatformPostV2['__typename']]: Array<
		EnrichedReadyToSharePlatformPostV2<Extract<ReadyToSharePlatformPostV2, { __typename: K }>>
	>;
}[ReadyToSharePlatformPostV2['__typename']];

// Add metadata to each post and filter out images where processing is true
// so that posts with images only contain processed images.
function mapPost(post: ReadyToSharePlatformPostV2): EnrichedReadyToSharePlatformPostV2 {
	const metadata = POST_TYPE_METADATA[post.__typename];

	if (!('images' in post)) return { ...post, ...metadata };

	const images: readonly ReadyToShareProcessedPostImage[] = post.images.filter(
		(image): image is ReadyToShareProcessedPostImage => !image.processing,
	);

	// oxlint-disable-next-line typescript/no-unsafe-type-assertion -- conditional type can't be verified at this level
	return { ...post, ...metadata, hasImage: images.length > 0, images } as unknown as EnrichedReadyToSharePlatformPostV2;
}

export async function getPetitionReadyToSharePostsV2(
	variables: PetitionReadyToSharePostsV2QueryVariables,
	{ gql: { fetch }, errorReporter }: UtilityContext,
): Promise<EnrichedReadyToSharePlatformPostsV2> {
	const { data, errors, requestId } = await fetch<
		PetitionReadyToSharePostsV2Query,
		PetitionReadyToSharePostsV2QueryVariables
	>({
		query: PetitionReadyToSharePostsV2,
		variables,
		rejectOnError: false,
	});

	if (errors || !data?.petitionBySlugOrId) {
		const error = new Error(`Failed to fetch Ready to Share V2 posts for petition ${variables.slugOrId}`);
		void errorReporter.report({ error, context: { requestId, variables, errors } });
		throw error;
	}

	const { readyToSharePostsV2 } = data.petitionBySlugOrId;

	if (readyToSharePostsV2.__typename === 'ReadyToSharePostsFailureV2') {
		// RESOURCE_NOT_FOUND is expected (not all petitions have RTS data); report as
		// warning to preserve visibility without triggering error-level alerts.
		const severity = readyToSharePostsV2.reason === 'RESOURCE_NOT_FOUND' ? 'warning' : undefined;
		void errorReporter.report(
			{
				error: new Error(`Failed to fetch Ready to Share V2 posts for petition ${variables.slugOrId}`),
				context: { requestId, variables, failure: readyToSharePostsV2 },
			},
			{ severity },
		);
		if (readyToSharePostsV2.reason === 'UNRECOVERABLE_ERROR') {
			throw new UnrecoverableError(readyToSharePostsV2.message);
		}
		return [];
	}

	// oxlint-disable-next-line typescript/no-unsafe-type-assertion -- homogeneous array narrowed by the `type` query param
	return readyToSharePostsV2.posts.map(mapPost) as EnrichedReadyToSharePlatformPostsV2;
}
